/**
 * The labelling report (GET /admin/v1/label-report): how much of an organisation's content carries a sensitivity
 * label, across memory, sessions, projects and portfolios, over a window.
 *
 * AIOE's own four levels (public, internal, confidential, restricted) are always known. Microsoft Purview label
 * names are known only for what machines report: a session that met a labelled file or answer. A memory item carries
 * a level, never a Purview name, and is never unlabelled (an unlabelled item takes the project's default level).
 * The report counts; it never carries content.
 */
import { z } from "zod";
import { SENSITIVITIES } from "./sensitivityLabels.js";
export const LABEL_REPORT_ROUTE = "/admin/v1/label-report";
export const LABEL_REPORT_DEFAULT_DAYS = 30;
export const LABEL_REPORT_MAX_DAYS = 365;
/** Project and portfolio rows beyond these are left out of the lists (the totals still count them). */
export const LABEL_REPORT_MAX_ROWS = 500;
export const labelReportQuerySchema = z.object({
    days: z.coerce.number().int().min(1).max(LABEL_REPORT_MAX_DAYS).default(LABEL_REPORT_DEFAULT_DAYS),
    projectId: z.string().min(1).max(200).optional(),
    businessAreaId: z.string().min(1).max(100).optional(),
});
const count = z.number().int().nonnegative();
/** One count per level. */
export const levelCountsSchema = z.object({ public: count, internal: count, confidential: count, restricted: count });
export const emptyLevelCounts = () => ({ public: 0, internal: 0, confidential: 0, restricted: 0 });
export const sumLevelCounts = (c) => SENSITIVITIES.reduce((n, l) => n + c[l], 0);
export const labelReportProjectRowSchema = z.object({
    projectId: z.string(),
    name: z.string(),
    businessAreaId: z.string().nullable(),
    businessAreaName: z.string().nullable(),
    /** The project's own classification; null is unclassified. */
    classification: z.enum(SENSITIVITIES).nullable(),
    memoryItems: count,
    memoryByLevel: levelCountsSchema,
    sessions: count,
    labelledSessions: count,
});
export const labelReportAreaRowSchema = z.object({
    /** Null for projects filed under no business area. */
    businessAreaId: z.string().nullable(),
    name: z.string(),
    projects: count,
    classified: count,
    unclassified: count,
    classificationByLevel: levelCountsSchema,
    memoryItems: count,
    memoryByLevel: levelCountsSchema,
    sessions: count,
    labelledSessions: count,
});
export const labelReportPurviewLabelSchema = z.object({
    /** The label as the file or answer named it. */
    name: z.string(),
    /** The level the organisation maps it to, when the report can tell. */
    sensitivity: z.enum(SENSITIVITIES).nullable(),
    sessions: count,
    events: count,
});
export const labelReportDaySchema = z.object({
    /** UTC day, YYYY-MM-DD. */
    day: z.string(),
    /** Memory items written or changed that day, by level. */
    memoryByLevel: levelCountsSchema,
    /** People relabelling memory that day. */
    relabels: count,
    sessions: count,
    labelledSessions: count,
});
export const labelReportSchema = z.object({
    generatedAt: z.string(),
    window: z.object({ from: z.string(), to: z.string(), days: z.number().int() }),
    filter: z.object({ projectId: z.string().optional(), businessAreaId: z.string().optional() }),
    purview: z.object({
        /** Purview is connected: label names below come from the organisation's own Microsoft labels. */
        connected: z.boolean(),
        labels: count,
        mapped: count,
    }),
    /** Labelled share of what can be unlabelled: sessions and projects (every memory item always carries a level). */
    coverage: z.object({ labelled: count, unlabelled: count, percent: z.number().min(0).max(100) }),
    memory: z.object({
        total: count,
        byLevel: levelCountsSchema,
        /** Who set the level: a person (pinned), an agent's write, or the project's rules and default. */
        byOrigin: z.object({ person: count, agent: count, rulesOrDefault: count }),
        /** Items that also carry compartments. */
        withCompartments: count,
    }),
    sessions: z.object({
        total: count,
        labelled: count,
        unlabelled: count,
        /** Labelled sessions by the highest level they met. */
        byLevel: levelCountsSchema,
        /** Purview labels machines named, most met first. */
        purviewLabels: z.array(labelReportPurviewLabelSchema),
    }),
    projects: z.object({
        total: count,
        classified: count,
        unclassified: count,
        byClassification: levelCountsSchema,
    }),
    /** Memory in the organisation's own index, belonging to no project (left out under a project or area filter). */
    organisationMemory: z.object({ total: count, byLevel: levelCountsSchema }),
    byProject: z.array(labelReportProjectRowSchema),
    byBusinessArea: z.array(labelReportAreaRowSchema),
    series: z.array(labelReportDaySchema),
    /** True when a list was cut at LABEL_REPORT_MAX_ROWS. */
    truncated: z.boolean(),
});
//# sourceMappingURL=labelReport.js.map